import React, { useEffect, useState } from "react";
import axios from "axios";
import { Check, Loader2, Plus } from "lucide-react";
import { toast } from "react-toastify";
import CustomToast from "../../misc/CustomToast";
import { Button } from "../../misc/Button";
import { Switch } from "../../misc/Switch";
import { Card } from "../../misc/Card";
import { getAuthHeader } from "../../../utils/authHeader";
import { cn } from "../../../utils/classMerger";
import { ListingType } from "../../../types/userListing";
import SlideshowLogoSelector from "./SlideshowLogoSelector";
import PreviewOverlay from "./PreviewOverlay";

// An overlay is a reusable Overlay asset (the property address ± a logo, in a
// chosen theme). GET /assets/overlays/:id returns { id, url }; POST
// /listing/overlay returns { _id, url }.
interface OverlayItem {
    id: string;
    url: string;
}

interface SlideshowOverlaySectionProps {
    listingId: string;
    listingType: ListingType;
    // The selected overlay's asset id (sent to the slideshow), or null for none.
    selectedOverlay: string | null;
    setSelectedOverlay: (id: string | null) => void;
    // Reports the selected overlay's resolved asset URL (or null) so the Classic
    // live preview can composite it over the photos.
    onSelectedOverlayUrlChange?: (url: string | null) => void;
    // Live preview inputs for the create form: the listing address rendered in
    // the overlay, and a sample photo to render it over.
    formattedAddress?: string;
    sampleImage?: string;
    // Lifted so the parent can present the create form inline (hiding the rest
    // of the branding step) instead of opening a second popup.
    creating: boolean;
    setCreating: (creating: boolean) => void;
}

const OverlayTile: React.FC<{
    url?: string;
    label: string;
    selected: boolean;
    onSelect: () => void;
}> = ({ url, label, selected, onSelect }) => (
    <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        title={label}
        className={cn(
            "relative flex items-center justify-center w-28 h-16 rounded-md border-2 overflow-hidden transition-all",
            url ? "bg-primary" : "bg-white",
            selected
                ? "border-secondary"
                : "border-neutral-200 hover:border-neutral-300"
        )}
    >
        {url ? (
            <img
                src={url}
                alt={label}
                decoding="async"
                className="max-w-full max-h-full object-contain"
            />
        ) : (
            <span className="text-xs font-medium text-neutral-500">{label}</span>
        )}
        {selected && (
            <span className="absolute top-1 right-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-secondary text-primary shadow">
                <Check className="w-3 h-3" />
            </span>
        )}
    </button>
);

// Classic (Video 1) overlay picker: reuse a saved overlay or create a new one
// (address text, with or without a logo, light/dark). Creating persists the
// overlay as an asset so it's reusable on future slideshows.
const SlideshowOverlaySection: React.FC<SlideshowOverlaySectionProps> = ({
    listingId,
    listingType,
    selectedOverlay,
    setSelectedOverlay,
    onSelectedOverlayUrlChange,
    formattedAddress = "",
    sampleImage = "",
    creating,
    setCreating,
}) => {
    const [overlays, setOverlays] = useState<OverlayItem[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [withLogo, setWithLogo] = useState(false);
    const [theme, setTheme] = useState<"light" | "dark">("light");
    const [createLogo, setCreateLogo] = useState<string | null>(null);

    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        const fetchOverlays = async (): Promise<void> => {
            try {
                const headers = await getAuthHeader();
                const { data } = await axios.get<OverlayItem[]>(
                    `${backendUrl}/assets/overlays/${listingId}`,
                    { headers }
                );
                setOverlays(Array.isArray(data) ? data : []);
            } catch {
                // Leave the list empty on failure — the picker degrades gracefully.
            }
        };
        if (listingId) fetchOverlays();
    }, [listingId, backendUrl]);

    // Surface the selected overlay's URL to the parent (for the Classic preview).
    useEffect(() => {
        if (!onSelectedOverlayUrlChange) return;
        const url = selectedOverlay
            ? overlays.find((o) => o.id === selectedOverlay)?.url ?? null
            : null;
        onSelectedOverlayUrlChange(url);
    }, [selectedOverlay, overlays, onSelectedOverlayUrlChange]);

    const resetCreateForm = (): void => {
        setCreating(false);
        setCreateLogo(null);
        setWithLogo(false);
        setTheme("light");
    };

    const handleCreate = async (): Promise<void> => {
        if (withLogo && !createLogo) {
            toast.error(
                <CustomToast
                    message="Select a logo, or switch to text only."
                    type="error"
                />,
                { autoClose: 3000 }
            );
            return;
        }
        setIsSaving(true);
        try {
            const headers = await getAuthHeader();
            const { data } = await axios.post<{ _id: string; url: string }>(
                `${backendUrl}/listing/overlay`,
                {
                    listingId,
                    listingType,
                    theme,
                    logo: withLogo ? createLogo : undefined,
                },
                { headers }
            );
            const added: OverlayItem = { id: data._id, url: data.url };
            setOverlays((prev) => [added, ...prev]);
            setSelectedOverlay(added.id);
            resetCreateForm();
            toast.success(
                <CustomToast message="Overlay created." type="success" />,
                { autoClose: 3000 }
            );
        } catch {
            toast.error(
                <CustomToast
                    message="Couldn't create the overlay. Please try again."
                    type="error"
                />,
                { autoClose: 3000 }
            );
        } finally {
            setIsSaving(false);
        }
    };

    if (creating) {
        return (
            <div className="flex min-h-0 flex-1 flex-col">
                <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4 pt-4 min-[640px]:px-6 min-[768px]:px-8">
                    <div className="grid grid-cols-1 gap-6 min-[900px]:grid-cols-[3fr_2fr] min-[900px]:items-start">
                        <Card>
                            <div className="space-y-4 p-4">
                                <div>
                                    <h4 className="text-base font-semibold text-primary">
                                        Create Address Overlay
                                    </h4>
                                    <p className="text-sm text-neutral-500">
                                        Show the property address over the
                                        video, with or without a logo.
                                    </p>
                                </div>

                                <div className="flex items-center justify-between gap-4 border-t border-neutral-200 pt-4">
                                    <div>
                                        <h5 className="text-sm font-medium text-primary">
                                            Include logo
                                        </h5>
                                        <p className="text-xs text-neutral-500">
                                            Off shows the address as text only.
                                        </p>
                                    </div>
                                    <Switch
                                        checked={withLogo}
                                        onCheckedChange={setWithLogo}
                                        checkedLabel="Logo"
                                        uncheckedLabel="Text"
                                        aria-label="Include logo in overlay"
                                    />
                                </div>

                                {withLogo && (
                                    <div className="border-t border-neutral-200 pt-4">
                                        <SlideshowLogoSelector
                                            selectedLogo={createLogo}
                                            setSelectedLogo={setCreateLogo}
                                        />
                                    </div>
                                )}

                                <div className="flex items-center justify-between gap-4 border-t border-neutral-200 pt-4">
                                    <div>
                                        <h5 className="text-sm font-medium text-primary">
                                            Theme
                                        </h5>
                                        <p className="text-xs text-neutral-500">
                                            Backdrop and text color.
                                        </p>
                                    </div>
                                    <Switch
                                        checked={theme === "dark"}
                                        onCheckedChange={(value) =>
                                            setTheme(value ? "dark" : "light")
                                        }
                                        checkedLabel="Dark"
                                        uncheckedLabel="Light"
                                        aria-label="Overlay theme"
                                    />
                                </div>
                            </div>
                        </Card>

                        <div>
                            <div className="min-[900px]:sticky min-[900px]:top-0">
                                <PreviewOverlay
                                    formattedAddress={formattedAddress}
                                    newOverlayHasLogo={withLogo}
                                    logoFile={null}
                                    logoUrl={createLogo}
                                    selectedLogo={null}
                                    logos={[]}
                                    theme={theme}
                                    image={sampleImage}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex shrink-0 justify-end gap-2 border-t border-neutral-200 bg-white px-4 py-3 min-[640px]:px-6 min-[768px]:px-8">
                    <Button
                        variant="secondary"
                        size="sm"
                        onClick={resetCreateForm}
                        disabled={isSaving}
                    >
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        onClick={handleCreate}
                        disabled={isSaving}
                        className="flex items-center justify-center"
                    >
                        {isSaving ? (
                            <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Creating…
                            </>
                        ) : (
                            "Create overlay"
                        )}
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div>
                <h4 className="text-base font-semibold text-primary">
                    Address Overlay
                </h4>
                <p className="text-sm text-neutral-500">
                    Optionally show the property address over the video. Reuse a
                    saved overlay, or create a new one (with or without a logo).
                </p>
            </div>

            <div className="flex flex-wrap gap-3">
                <OverlayTile
                    label="None"
                    selected={!selectedOverlay}
                    onSelect={() => setSelectedOverlay(null)}
                />
                {overlays.map((overlay) => (
                    <OverlayTile
                        key={overlay.id}
                        url={overlay.url}
                        label="Overlay"
                        selected={selectedOverlay === overlay.id}
                        onSelect={() => setSelectedOverlay(overlay.id)}
                    />
                ))}
                <button
                    type="button"
                    onClick={() => setCreating(true)}
                    className="flex flex-col items-center justify-center w-28 h-16 rounded-md border-2 border-dashed border-neutral-300 text-neutral-500 hover:border-secondary hover:text-primary transition-colors"
                >
                    <Plus className="h-5 w-5 mb-1 text-secondary" />
                    <span className="text-xs font-medium">Create</span>
                </button>
            </div>
        </div>
    );
};

export default SlideshowOverlaySection;
